import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  UserGroupIcon,
  MagnifyingGlassIcon,
  TicketIcon,
  CalendarIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline'
import { Card, Badge, Avatar, InlineSpinner, EmptyState, Pagination } from '@/components/ui'
import { taskforceApi } from '@/api'
import type { TaskForce } from '@/api/taskforce'

export default function TaskForcesPage() {
  const [taskforces, setTaskforces] = useState<TaskForce[]>([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState<'active' | 'completed' | ''>('')
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalItems, setTotalItems] = useState(0)

  useEffect(() => {
    fetchTaskForces()
  }, [currentPage, searchQuery, statusFilter])

  const fetchTaskForces = async () => {
    setLoading(true)
    try {
      const data = await taskforceApi.getTaskForces({
        page: currentPage,
        search: searchQuery || undefined,
        status: statusFilter || undefined,
      })
      setTaskforces(data.results || [])
      setTotalPages(Math.ceil((data.count || 0) / 12))
      setTotalItems(data.count || 0)
    } catch (error) {
      console.error('Error fetching task forces:', error)
      setTaskforces([])
    } finally {
      setLoading(false)
    }
  }

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    setCurrentPage(1)
    fetchTaskForces()
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('it-IT', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    })
  }

  if (loading && currentPage === 1) {
    return <InlineSpinner message="Caricamento task force..." />
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Task Force</h1>
          <p className="text-gray-600">Gruppi di lavoro della community</p>
        </div>
        <Link
          to="/taskforce/my"
          className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
        >
          <UserGroupIcon className="h-5 w-5 mr-2" />
          Le mie Task Force
        </Link>
      </div>

      {/* Search and Filters */}
      <Card>
        <form onSubmit={handleSearch} className="flex flex-wrap gap-4">
          <div className="flex-1 min-w-[200px] relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Cerca task force..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => { setStatusFilter(''); setCurrentPage(1); }}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                !statusFilter ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Tutte
            </button>
            <button
              type="button"
              onClick={() => { setStatusFilter('active'); setCurrentPage(1); }}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                statusFilter === 'active' ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Attive
            </button>
            <button
              type="button"
              onClick={() => { setStatusFilter('completed'); setCurrentPage(1); }}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                statusFilter === 'completed' ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Completate
            </button>
          </div>
          <button
            type="submit"
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            Cerca
          </button>
        </form>
      </Card>

      {/* Task Forces Grid */}
      {taskforces.length > 0 ? (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {taskforces.map((tf) => (
              <Link key={tf.id} to={`/taskforce/${tf.slug}`} className="group">
                <Card className="h-full hover:shadow-lg transition-shadow">
                  {/* Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
                        <UserGroupIcon className="h-6 w-6 text-primary-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900 group-hover:text-primary-600 transition-colors">
                          {tf.name}
                        </h3>
                        <Badge variant={tf.is_active ? 'success' : 'default'} size="sm">
                          {tf.is_active ? 'Attiva' : 'Completata'}
                        </Badge>
                      </div>
                    </div>
                    {tf.is_member && (
                      <Badge variant="info" size="sm">
                        <CheckCircleIcon className="h-3 w-3 mr-1" />
                        Membro
                      </Badge>
                    )}
                  </div>

                  {/* Description */}
                  <p className="text-sm text-gray-500 line-clamp-3 mb-4">
                    {tf.description}
                  </p>

                  {/* Leader */}
                  <div className="flex items-center gap-2 mb-4">
                    <Avatar size="sm" name={tf.leader.full_name} src={tf.leader.avatar || undefined} />
                    <div>
                      <p className="text-xs text-gray-500">Leader</p>
                      <p className="text-sm font-medium text-gray-900">{tf.leader.full_name}</p>
                    </div>
                  </div>

                  {/* Stats */}
                  <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                    <div className="flex items-center gap-4 text-sm text-gray-500">
                      <span className="flex items-center gap-1">
                        <UserGroupIcon className="h-4 w-4" />
                        {tf.members_count}
                      </span>
                      <span className="flex items-center gap-1">
                        <TicketIcon className="h-4 w-4" />
                        {tf.tickets_count}
                      </span>
                    </div>
                    <div className="text-xs text-gray-400">
                      <CalendarIcon className="h-3 w-3 inline mr-1" />
                      {formatDate(tf.created_at)}
                    </div>
                  </div>
                </Card>
              </Link>
            ))}
          </div>

          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={totalItems}
            itemsPerPage={12}
            onPageChange={setCurrentPage}
          />
        </>
      ) : (
        <Card>
          <EmptyState
            icon={<UserGroupIcon className="h-12 w-12" />}
            title={searchQuery || statusFilter ? 'Nessuna task force trovata' : 'Nessuna task force'}
            description={
              searchQuery || statusFilter
                ? 'Prova a modificare i filtri di ricerca'
                : 'Le task force saranno disponibili a breve'
            }
          />
        </Card>
      )}
    </div>
  )
}
